import { AlertTitle, Button } from "@mui/material";
import TruckInfo from "components/Trucks/TruckInfo/TruckInfo";
import TruckShippingInfo from "components/Trucks/TruckShippingInfo/TruckShippingInfo";
import TrucksHeader from "components/Trucks/TrucksHeader/TrucksHeader";
import { TruckStepOneSchema } from "components/Trucks/stepOneSchema";
import { truckActions } from "features/reducers/truck/truck";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { models } from "types/api/viewModels";
import { CreateTrucksContent, TrucksErrorBanner } from "./CreateTrucksPage.styles";
import { useSaveTruckMutation } from "features/api/truckApi";
import CreateTrucksSkeleton from "./CreateTrucksSkeleton";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner";
import {
	AccountNumberText,
	CantProcessRequestText,
	ProductLineText,
	RequestDateText,
	SaveAndAddOrdersText,
	ServerTemporarilyUnavailableText,
	ShippingAddressText,
	SomethingDoesntLookRightText,
	TruckNameText,
	UniqueTruckErrorText
} from "constants/text";
import { ContactCare } from "components/NewOrders/constants";
import { useGetTrucksAccountsQuery } from "features/api/accountApi";
import { RootState } from "stores/application.store";
import { useEffect, useState } from "react";
import { useNewOrderShipToAddressesMutation } from "features/api/newOrderApi";

const CreateTrucksPage = () => {
	const {
		handleSubmit,
		formState: { errors },
		setError
	} = useFormContext();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { data: accounts, isLoading: isAccountsLoading } = useGetTrucksAccountsQuery("");
	const [saveTruck, { isLoading: isSavingTruck, isError: isSaveTruckError }] = useSaveTruckMutation();

	const [selectedAccountNumber, setSelectedAccountNumber] = useState<
		models["CustomerAccountViewModel"] | undefined
	>();
	const [newOrderShipToAddresses, { data: addresses, isLoading: addressesLoading }] =
		useNewOrderShipToAddressesMutation();

	const truck = useSelector((state: RootState) => state.truck.truck);

	// display specific text in error banner list for each error
	const errorArr = Object.keys(errors);
	const fieldNameMap: Record<string, string> = {
		productLine: ProductLineText,
		truckName: TruckNameText,
		accountNumber: AccountNumberText,
		requestDate: RequestDateText,
		stops: ShippingAddressText
	};
	const errorFields = [
		...errorArr.map((key) => fieldNameMap[key] ?? key).filter(Boolean),
		...(errors.accountNumber && !errors.stops ? [ShippingAddressText] : []),
		...(errors.accountNumber && !errors.productLine ? [ProductLineText] : [])
	];

	const handleSave = async (values: TruckStepOneSchema) => {
		const reduxStops = truck?.stops ?? [];
		const formStops = values.stops ?? [];

		const stopsMap = new Map<string | null | undefined, models["TruckStopViewModel"]>();
		reduxStops.forEach((stop) => {
			stopsMap.set(stop?.shipToId, stop);
		});
		formStops.forEach((stop) => {
			const key = stop?.shipToId;
			if (!stopsMap.has(key)) {
				stopsMap.set(key, stop);
			}
		});

		const mergedStops = Array.from(stopsMap.values());

		const createdTruck: models["TruckViewModel"] = {
			accountId: values.accountNumber?.accountId,
			accountName: values.accountNumber?.accountName,
			productLine: values.productLine ?? undefined,
			truckName: values.truckName,
			stops: mergedStops,
			requestedDeliveryDate: new Date(values.requestDate ?? "").toISOString(),
			truckId: truck?.truckId
		};

		try {
			const response = await saveTruck(createdTruck).unwrap();
			dispatch(truckActions.updateTruck(response!));
			navigate("/trucks/add-order");
		} catch (error: any) {
			console.error("Save truck failed:", error);
			dispatch(truckActions.updateTruck({ ...createdTruck, truckId: truck?.truckId }));
			if (error?.status === 409) {
				setError("truckName", { message: UniqueTruckErrorText });
			}
		}
	};

	// handle when account number is changed
	useEffect(() => {
		newOrderShipToAddresses(
			selectedAccountNumber?.billTos?.map((billTo) => ({
				billToId: billTo.billToId,
				accountId: selectedAccountNumber?.accountId
			})) ?? []
		)
			.unwrap()
			.catch((err) => err);
	}, [selectedAccountNumber, newOrderShipToAddresses]);

	return (
		<form // NOSONAR: need to prevent form submission on enter key
			data-testid="trucks-create-trucks-page"
			onSubmit={handleSubmit(handleSave)}
			noValidate
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault();
				}
			}}
		>
			<TrucksHeader
				isStepOne
				isLoading={isSavingTruck}
			/>
			<CreateTrucksContent>
				{(isSaveTruckError || errorArr.length > 0) && (
					<ErrorBanner
						errorMessaging={
							<TrucksErrorBanner>
								<AlertTitle>{CantProcessRequestText}</AlertTitle>
								<span>
									{errorArr.length > 0
										? SomethingDoesntLookRightText
										: ServerTemporarilyUnavailableText}
								</span>
								<ul>
									{errorFields.map((error) => (
										<li key={error}>{error}</li>
									))}
								</ul>
								{errorArr.length > 0 && <span>{ContactCare}</span>}
							</TrucksErrorBanner>
						}
					/>
				)}
				{isSavingTruck ? (
					<CreateTrucksSkeleton />
				) : (
					<>
						<TruckInfo
							accounts={accounts ?? []}
							isAccountsLoading={isAccountsLoading}
							setSelectedAccountNumber={setSelectedAccountNumber}
						/>
						<TruckShippingInfo
							isAccountsLoading={isAccountsLoading}
							addresses={addresses ?? []}
							isAddressesLoading={addressesLoading}
						/>
						<Button
							variant="contained"
							type="submit"
							data-testid="trucks-create-trucks-save-button"
						>
							{SaveAndAddOrdersText}
						</Button>
					</>
				)}
			</CreateTrucksContent>
		</form>
	);
};

export default CreateTrucksPage;
